/**
 * Load Board SSO handoff client.
 *
 * Asks the backend to mint a one-time-use signed JWT, then navigates
 * the browser to the load board's /sso receiver. No password leaves
 * TMS — the marketing site verifies the token, sets its own session,
 * and lands the user on its dashboard.
 *
 * Used in 3 places:
 *   - Sidebar nav link
 *   - User dropdown menu (top-right)
 *   - Admin "Sign in as" action on the Users page
 */
import axios from 'axios';
import { toast } from 'sonner';

export async function signInToLoadBoard({ API, token, targetUserId, targetUserName } = {}) {
  if (!API || !token) {
    toast.error('You need to be signed in to use this');
    return;
  }
  const url = targetUserId
    ? `${API}/auth/loadboard-handoff/${targetUserId}`
    : `${API}/auth/loadboard-handoff`;
  const loadingId = toast.loading(
    targetUserId
      ? `Signing into Load Board as ${targetUserName || 'user'}…`
      : 'Signing into Load Board…'
  );
  try {
    const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
    toast.dismiss(loadingId);
    // Open in a new tab so the user keeps their TMS session intact.
    const tab = window.open(res.data.redirect_url, '_blank', 'noopener,noreferrer');
    if (!tab) {
      toast.error("Couldn't open the Load Board — please allow pop-ups for this site.");
    } else if (res.data.impersonating) {
      toast.success(`Opened Load Board as ${targetUserName}`);
    } else {
      toast.success('Opened Load Board');
    }
  } catch (e) {
    toast.dismiss(loadingId);
    const detail = e?.response?.data?.detail || e?.message;
    toast.error(
      typeof detail === 'string'
        ? detail
        : 'Could not sign into Load Board — please contact support',
    );
  }
}
